import { useEffect, useRef, useState } from 'react';

/**
 * Hook לאנימציות scroll
 * 
 * שימוש:
 * const ref = useScrollAnimation();
 * 
 * <div ref={ref} className="scroll-fade-in">
 *   תוכן
 * </div>
 */

export type AnimationType = 'fadeIn' | 'slideInRight' | 'slideInLeft' | 'scaleIn';

interface UseScrollAnimationOptions {
  threshold?: number; // 0-1, כמה מהאלמנט צריך להיות גלוי
  rootMargin?: string; // margin סביב ה-root
  animationType?: AnimationType; // סוג האנימציה
  triggerOnce?: boolean; // האם להפעיל רק פעם אחת
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    animationType = 'fadeIn',
    triggerOnce = true,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  // הוספת class בהתאם לסוג האנימציה
  useEffect(() => {
    if (ref.current) {
      const element = ref.current;
      const baseClass = `scroll-${animationType}`;
      
      // הסרת כל ה-classes הקודמים
      element.classList.remove('scroll-fade-in', 'scroll-slide-in-right', 'scroll-slide-in-left', 'scroll-scale-in', 'visible');
      
      // הוספת class הנוכחי - מיד כשנוצר ה-ref
      element.classList.add(baseClass);
      
      // אם כבר גלוי, מוסיף visible מיד
      if (isVisible) {
        // Delay קצר כדי שהדפדפן יראה את השינוי
        setTimeout(() => {
          if (ref.current) {
            ref.current.classList.add('visible');
          }
        }, 10);
      }
    }
  }, [isVisible, animationType]);

  return ref;
};

/**
 * Hook פשוט יותר - רק מחזיר אם האלמנט גלוי
 */
export const useInView = (options: UseScrollAnimationOptions = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

